import React, { useState, useEffect } from "react";
import { useInventoryStore, GroupedInventoryItem } from "../../store/inventoryStore";
import { useProfileStore } from "../../store/profileStore";
import { useToast } from "../../components/ToastContainer";
import { useApiCall } from "../../hooks/useApiCall";
import { useGlobalLoading } from "../../hooks/useGlobalLoading";
import InventoryTable from "../../components/InventoryTable";
import Loader from "../../components/Loader";
import EditProductModal from "../../components/EditProductModal";
import AddProductModal from "../../components/AddProductModal";
import ConfirmModal from "../../components/ConfirmModal";
import BuyerModal from "../../components/BuyerModal";
import SetPlaceModal from "../../components/SetPlaceModal";
import ChangeStatusModal from "../../components/ChangeStatusModal";
import ShipmentModal from "../../components/ShipmentModal";

interface SneakersInventoryProps {
  searchQuery: string;
  onAddProduct: () => void;
}

const SneakersInventory: React.FC<SneakersInventoryProps> = ({
  searchQuery,
  onAddProduct,
}) => {
  const {
    groupedItems,
    isLoading,
    fetchInventory,
    fetchItemById,
    deleteItem,
    markAsSold,
    markAsHidden,
    markAsVisible,
    fetchShipments,
    currentPage: storePage,
    itemsPerPage: storeItemsPerPage,
    totalItems: storeTotalItems,
    searchInventory,
    searchResults,
    isSearching,
    clearSearchResults,
    searchCurrentPage,
    searchTotalItems,
    searchItemsPerPage,
    setSearchPage,
    setSearchItemsPerPage,
    setPage,
    setItemsPerPage,
  } = useInventoryStore();

  const { accessStockroom } = useProfileStore();
  const { showSuccess, showError } = useToast();
  const { executeApiCall } = useApiCall();
  const { addLoadingStep, removeLoadingStep } = useGlobalLoading();

  // Локальные состояния
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [loadingProductData, setLoadingProductData] = useState(false);

  // Состояния для модальных окон
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    qrCode?: string;
    productInfo?: any;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const [buyerModal, setBuyerModal] = useState<{
    isOpen: boolean;
    itemId: number | null;
    isLoading: boolean;
  }>({
    isOpen: false,
    itemId: null,
    isLoading: false,
  });

  const [setPlaceModal, setSetPlaceModal] = useState<{
    isOpen: boolean;
    productCode: string;
  }>({
    isOpen: false,
    productCode: "",
  });

  const [changeStatusModal, setChangeStatusModal] = useState<{
    isOpen: boolean;
    productCode: string;
  }>({
    isOpen: false,
    productCode: "",
  });

  const [shipmentModal, setShipmentModal] = useState<{
    isOpen: boolean;
    sneakerId: number;
    productName: string;
  }>({
    isOpen: false,
    sneakerId: 0,
    productName: "",
  });

  // Поиск
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        searchInventory(searchQuery);
      } else {
        clearSearchResults();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchInventory, clearSearchResults]);

  // Определяем текущие данные
  const isSearchMode = searchQuery.trim() !== "";
  const currentItems = isSearchMode ? searchResults : groupedItems;
  const isPaginationLoading = isSearchMode ? isSearching : isLoading;
  
  // Пагинация
  const currentPageToUse = isSearchMode ? searchCurrentPage + 1 : storePage + 1;
  const totalItemsToUse = isSearchMode ? searchTotalItems : storeTotalItems;
  const itemsPerPageToUse = isSearchMode ? searchItemsPerPage : storeItemsPerPage;

  // Функции форматирования
  const formatPrice = (price: string): string => {
    if (!price) return "0 ₽";
    
    // Проверяем, есть ли диапазон цен (содержит дефис или тире)
    if (price.includes('-') || price.includes('—')) {
      const parts = price.split(/[-—]/).map(part => part.trim());
      if (parts.length === 2) {
        const minPrice = parts[0].replace(/[^\d]/g, "");
        const maxPrice = parts[1].replace(/[^\d]/g, "");
        if (minPrice && maxPrice) {
          return `${parseInt(minPrice).toLocaleString()}\u00A0₽ - ${parseInt(maxPrice).toLocaleString()}\u00A0₽`;
        }
      }
    }
    
    // Если это одна цена
    const cleanPrice = price.replace(/[^\d]/g, "");
    if (!cleanPrice) return "0 ₽";
    return `${parseInt(cleanPrice).toLocaleString()}\u00A0₽`;
  };

  const formatMarketPrice = (price: string): string => {
    if (!price || price === "—" || price === "-") return "—";
    const cleanPrice = price.replace(/[^\d.,]/g, "");
    if (!cleanPrice) return "—";
    const numericPrice = parseFloat(cleanPrice.replace(",", "."));
    if (isNaN(numericPrice)) return "—";
    return `${numericPrice} ₽`;
  };

  // Обработчики
  const handleEdit = async (size: any, product: GroupedInventoryItem) => {
    setLoadingProductData(true);
    try {
      const actualProductData = await fetchItemById(size.id);
      setEditingProduct(actualProductData);
      setEditModalOpen(true);
    } catch (error) {
      const inventoryItem = {
        id: size.id,
        name: product.name,
        model: product.model,
        color: product.color,
        size: size.size,
        price: size.price,
        article: product.article,
        avgMarketPrice: size.avgMarketPrice,
        maxPrice: size.maxPrice,
        minPrice: size.minPrice,
        cost: size.cost,
        goatPrice: size.goatPrice,
        stockXPrice: size.stockXPrice,
        poizonPrice: size.poizonPrice,
        imageUrl: product.imageUrl,
        condition: size.condition,
        city: size.city,
        place: size.place,
        fitting: size.fitting,
        vendor: size.vendor,
        inStore: size.inStore,
        isSale: size.isSale,
        saleDate: size.saleDate,
        purchaseDate: size.purchaseDate,
      };
      setEditingProduct(inventoryItem);
      setEditModalOpen(true);
    } finally {
      setLoadingProductData(false);
    }
  };

  const handleDelete = (id: number) => {
    setConfirmModal({
      isOpen: true,
      title: "Удаление товара",
      message: "Вы уверены, что хотите удалить этот товар? Это действие нельзя отменить.",
      onConfirm: async () => {
        await executeApiCall(() => deleteItem(id), {
          successMessage: "Товар успешно удален из инвентаря",
        });
        setConfirmModal((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleSold = (id: number) => {
    setBuyerModal({
      isOpen: true,
      itemId: id,
      isLoading: false,
    });
  };

  const handleBuyerConfirm = async (buyer: string) => {
    if (!buyerModal.itemId) return;
    setBuyerModal((prev) => ({ ...prev, isLoading: true }));
    try {
      await executeApiCall(() => markAsSold(buyerModal.itemId!, buyer), {
        successMessage: "Товар успешно отмечен как проданный",
      });
      setBuyerModal({ isOpen: false, itemId: null, isLoading: false });
    } catch (error) {
      setBuyerModal((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleHide = async (id: number, isCurrentlyInStore: boolean) => {
    if (isCurrentlyInStore) {
      setConfirmModal({
        isOpen: true,
        title: "Скрытие товара",
        message: "Товар пропадет с маркета и ВК бота. Вы уверены, что хотите скрыть этот товар?",
        onConfirm: async () => {
          await executeApiCall(() => markAsHidden(id), {
            successMessage: "Товар успешно скрыт из магазина",
          });
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        },
      });
    } else {
      await executeApiCall(() => markAsVisible(id), {
        successMessage: "Товар успешно добавлен в магазин",
      });
    }
  };

  const handleShipment = (sneakerId: number, productName: string) => {
    setShipmentModal({ isOpen: true, sneakerId, productName });
  };

  const handleShipmentConfirm = async (statusId: number) => {
    await fetchInventory(storePage * storeItemsPerPage, storeItemsPerPage);
    await fetchShipments();
    showSuccess("Товар успешно отгружен");
  };

  const handleCopyLink = async (id: number) => {
    const link = `${window.location.origin}/sneaker/${id}`;
    try {
      await navigator.clipboard.writeText(link);
      showSuccess("Ссылка скопирована в буфер обмена");
    } catch (error) {
      showError("Не удалось скопировать ссылку");
    }
  };

  // Обработчики пагинации
  const handlePageChange = (pageNumber: number) => {
    if (isSearchMode) {
      setSearchPage(pageNumber - 1, false); // API использует 0-based индекс, false = инвентарь
    } else {
      setPage(pageNumber - 1);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    if (isSearchMode) {
      setSearchItemsPerPage(newItemsPerPage, false);
    } else {
      setItemsPerPage(newItemsPerPage);
    }
  };

  const handleGeneratePromoCode = async (id: number) => {
    try {
      addLoadingStep("generate-promo", "Генерируем промокод...", 5);
      const { api } = await import("../../api/api");
      
      const result = await executeApiCall(
        () => api.initProductInStockroom(id),
        {
          successMessage: "Промокод успешно сгенерирован!",
          errorMessage: "Ошибка при генерации промокода",
        }
      );

      if (result) {
        try {
          const productInfo = await executeApiCall(
            () => api.getStockroomItem(result.product_code),
            { errorMessage: "Ошибка при получении информации о продукте" }
          );

          setConfirmModal({
            isOpen: true,
            title: "Информация о продукте",
            message: `Код товара: ${result.product_code}`,
            qrCode: result.qr_code,
            productInfo: productInfo ?? undefined,
            onConfirm: () => setConfirmModal((prev) => ({ ...prev, isOpen: false })),
          });
        } catch {
          setConfirmModal({
            isOpen: true,
            title: "Информация о продукте",
            message: `Код товара: ${result.product_code}`,
            qrCode: result.qr_code,
            onConfirm: () => setConfirmModal((prev) => ({ ...prev, isOpen: false })),
          });
        }
      }
    } catch (error) {
      showError("Ошибка при генерации промокода");
    } finally {
      removeLoadingStep("generate-promo");
    }
  };

  if (isPaginationLoading && currentItems.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "300px",
        }}
      >
        <Loader size="medium" text="Загружаем страницу..." />
      </div>
    );
  }

  return (
    <>
      <InventoryTable
        data={currentItems as GroupedInventoryItem[]}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onSold={handleSold}
        onHide={handleHide}
        onShipment={handleShipment}
        onCopyLink={handleCopyLink}
        onGeneratePromoCode={handleGeneratePromoCode}
        formatPrice={formatPrice}
        formatMarketPrice={formatMarketPrice}
        loadingProductData={loadingProductData}
        accessStockroom={accessStockroom}
        currentPage={currentPageToUse}
        totalItems={totalItemsToUse}
        itemsPerPage={itemsPerPageToUse}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      <EditProductModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingProduct(null);
        }}
        product={editingProduct}
        isLoading={loadingProductData}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        qrCode={confirmModal.qrCode}
        productInfo={confirmModal.productInfo}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        onChangeLocation={() => {
          if (confirmModal.productInfo?.product_code) {
            setSetPlaceModal({
              isOpen: true,
              productCode: confirmModal.productInfo.product_code,
            });
          }
        }}
        onChangeStatus={() => {
          if (confirmModal.productInfo?.product_code) {
            setChangeStatusModal({
              isOpen: true,
              productCode: confirmModal.productInfo.product_code,
            });
          }
        }}
      />

      <BuyerModal
        isOpen={buyerModal.isOpen}
        onConfirm={handleBuyerConfirm}
        onCancel={() => setBuyerModal({ isOpen: false, itemId: null, isLoading: false })}
        isLoading={buyerModal.isLoading}
      />

      <SetPlaceModal
        isOpen={setPlaceModal.isOpen}
        productCode={setPlaceModal.productCode}
        onClose={() => setSetPlaceModal({ isOpen: false, productCode: "" })}
        onSuccess={async () => {
          if (confirmModal.productInfo?.product_code) {
            try {
              const { api } = await import("../../api/api");
              const updatedProductInfo = await api.getStockroomItem(
                confirmModal.productInfo.product_code
              );
              setConfirmModal((prev) => ({ ...prev, productInfo: updatedProductInfo }));
            } catch (error) {
              console.error("Ошибка при обновлении информации о продукте:", error);
            }
          }
        }}
      />

      <ChangeStatusModal
        isOpen={changeStatusModal.isOpen}
        productCode={changeStatusModal.productCode}
        onClose={() => setChangeStatusModal({ isOpen: false, productCode: "" })}
        onSuccess={async () => {
          setChangeStatusModal({ isOpen: false, productCode: "" });
          if (confirmModal.productInfo?.product_code) {
            try {
              const { api } = await import("../../api/api");
              const updatedProductInfo = await api.getStockroomItem(
                confirmModal.productInfo.product_code
              );
              setConfirmModal((prev) => ({ ...prev, productInfo: updatedProductInfo }));
            } catch (error) {
              console.error("Ошибка при обновлении информации о продукте:", error);
            }
          }
        }}
      />

      <ShipmentModal
        isOpen={shipmentModal.isOpen}
        sneakerId={shipmentModal.sneakerId}
        productName={shipmentModal.productName}
        onClose={() => setShipmentModal({ isOpen: false, sneakerId: 0, productName: "" })}
        onConfirm={handleShipmentConfirm}
      />
    </>
  );
};

export default SneakersInventory;
